import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { paths } from '../../app/routes'
import type { CachedItem, LocalResponse, LocalSession } from '../../db/types'
import { remainingFor, serverNow, type Remaining } from '../../offline/clock'
import { completeSession, loadSessionRun, recordAnswer } from '../../offline/session'
import { useRepaintTick } from '../../shared/hooks'

export interface SessionRunnerState {
  /** `null` while loading, and for a session id IndexedDB does not hold. */
  session: LocalSession | null
  items: CachedItem[]
  index: number
  item: CachedItem | undefined
  /** The stored response for the current item, once it has been checked. */
  answered: LocalResponse | undefined
  answeredCount: number
  isLast: boolean
  remaining: Remaining
  /** The current, unchecked answer. */
  draft: unknown
  setDraft(answer: unknown): void
  busy: boolean
  goTo(index: number): void
  check(): Promise<void>
  finish(): Promise<void>
}

const UNTIMED: Remaining = { remainingMs: null, expired: false }

/**
 * Everything on the session screen that is not the item itself: loading and
 * resuming from IndexedDB, the per-item clock, recording answers and finishing.
 *
 * The renderer is handed no correct answer and no explanation (§9 rule 1); the
 * runner evaluates (through `recordAnswer`) and the screen shows the explanation.
 */
export function useSessionRunner(sessionId: string): SessionRunnerState {
  const navigate = useNavigate()

  const [session, setSession] = useState<LocalSession | null>(null)
  const [items, setItems] = useState<CachedItem[]>([])
  const [responses, setResponses] = useState<Map<string, LocalResponse>>(new Map())
  const [index, setIndex] = useState(0)
  const [draft, setDraft] = useState<unknown>(undefined)
  const [busy, setBusy] = useState(false)

  const shownAt = useRef<number>(serverNow())

  useEffect(() => {
    // Loads from IndexedDB. Moves to a query hook with the Phase 4 data-layer work.
    let cancelled = false
    void loadSessionRun(sessionId).then((run) => {
      if (cancelled || !run) return
      setSession(run.session)
      setItems(run.items)
      setResponses(run.responses)
      setIndex(run.resumeIndex)
      setDraft(undefined)
    })
    return () => {
      cancelled = true
    }
  }, [sessionId])

  /** Moving to another item discards the unchecked answer on the current one. */
  const goTo = useCallback((next: number) => {
    setIndex(next)
    setDraft(undefined)
  }, [])

  useEffect(() => {
    shownAt.current = serverNow()
  }, [index])

  // §11: recomputed on every render, and the ticker exists only to force renders.
  useRepaintTick(500, session !== null && session.durationSec !== null)
  const remaining = session ? remainingFor(session) : UNTIMED

  const settle = useCallback(async () => {
    setBusy(true)
    try {
      await completeSession(sessionId)
      navigate(paths.result(sessionId), { replace: true })
    } finally {
      setBusy(false)
    }
  }, [navigate, sessionId])

  // §11: if a return from background finds the deadline passed, the session
  // auto-submits with the responses recorded so far.
  const status = session?.status
  useEffect(() => {
    if (status !== 'active' || !remaining.expired) return
    console.info('[session] deadline passed while away — auto-submitting (§11)')
    // Reacting to the clock is what this effect is for.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void settle()
  }, [status, remaining.expired, settle])

  const item = items[index]

  const check = useCallback(async () => {
    if (!item || !session || draft === undefined) return
    setBusy(true)
    try {
      // §6.1: written to IndexedDB before any UI transition.
      const response = await recordAnswer({
        session,
        item,
        answer: draft,
        // §11: per-item time is measured from timestamps too, never accumulated.
        timeSpentMs: Math.max(0, serverNow() - shownAt.current),
      })
      setResponses((prev) => new Map(prev).set(item.id, response))
    } finally {
      setBusy(false)
    }
  }, [item, session, draft])

  return {
    session,
    items,
    index,
    item,
    answered: item ? responses.get(item.id) : undefined,
    answeredCount: responses.size,
    isLast: index === items.length - 1,
    remaining,
    draft,
    setDraft,
    busy,
    goTo,
    check,
    finish: settle,
  }
}
